import type { Entitlement } from '@collinsonx/utils/generatedTypes/graphql';

import { useLazyQuery } from '@collinsonx/utils/apollo';
import { getEntitlementsForProduct } from '@collinsonx/utils/queries';
import { getItem, JSONParse, removeItem, setItem } from '@lib';
import { OriginType, sendInfoLog, sendWarnLog } from '@lib/logging';
import { BookingInfo, BookingWarn } from 'config/datadog';
import { ENTITLEMENTS, NEW_MEMBERSHIP_ENROLLED } from 'config/storage';
import useBooking from 'hooks/useBooking';
import { useEffect } from 'react';
import { executePolling } from 'utils/poll';

import useGetEntitlements from './useGetEntitlements';
import { useHandleUserValidation } from './useHandleUserValidation';
import usePayload from './usePayload';
import useProduct from './useProduct';

const useHandleEntitlements = () => {
  const { setEntitlements, setEntitlementsLoading } = useBooking();
  const { productID } = useProduct();
  const { consumerID, membership, payload } = usePayload();
  const { hasPermissionsForEntitlements } = useGetEntitlements();
  const { isUserValidated } = useHandleUserValidation();

  const { accountProvider } = payload || {};
  const programme = accountProvider ?? null;

  const validateEntitlementsResponse = (data?: {
    getEntitlementsForProduct: Entitlement[];
  }) => {
    if (!data) return [];
    const entitlementsRes = data.getEntitlementsForProduct;

    if (!entitlementsRes || !entitlementsRes.length) return [];

    return entitlementsRes;
  };

  const [
    fetchEntitlementsRequest,
    { data: entitlementsData, loading: entitlementsLoading },
  ] = useLazyQuery<{
    getEntitlementsForProduct: Entitlement[];
  }>(getEntitlementsForProduct, {
    variables: {
      consumerId: consumerID,
      membershipID: membership?.id,
      productId: productID,
      programme,
    },
  });

  const readyToFetchEntitlements = Boolean(
    isUserValidated &&
      membership?.id &&
      productID &&
      programme &&
      hasPermissionsForEntitlements &&
      consumerID
  );

  const fetchEntitlements = async () => {
    if (!readyToFetchEntitlements) return null;

    const reponse = await fetchEntitlementsRequest();
    return reponse.data?.getEntitlementsForProduct;
  };

  const pollEntitlements = async () => {
    const newMembershipEnrolled = getItem(NEW_MEMBERSHIP_ENROLLED) === 'true';

    if (!newMembershipEnrolled) {
      setEntitlementsLoading(true);
      try {
        await fetchEntitlements();
      } catch {
        sendWarnLog(BookingWarn.FAILED_TO_FETCH_ENTITLEMENTS, {
          origin: OriginType.API,
        });
      } finally {
        setEntitlementsLoading(false);
      }
      return;
    }

    removeItem(NEW_MEMBERSHIP_ENROLLED);
    setEntitlementsLoading(true);
    const { attempts, result, stoppedReason } = await executePolling(
      fetchEntitlements,
      {
        onComplete: () => setEntitlementsLoading(false),
        onError: () =>
          sendWarnLog(BookingWarn.FAILED_TO_FETCH_ENTITLEMENTS, {
            origin: OriginType.API,
          }),
        shouldStop: (r) =>
          Boolean((r?.length ?? 0) >= 4 || r?.[0]?.isUnlimited),
      }
    );

    sendInfoLog(BookingInfo.ENTITLEMENTS_POLL_RESULT, {
      attempts,
      entitlementsCount: result?.length ?? 0,
      isUnlimited: Boolean(result?.[0]?.isUnlimited),
      stoppedReason,
    });
  };

  const getEntitlements = () => {
    const cachedEntitlements = JSONParse(getItem(ENTITLEMENTS), false);

    if (cachedEntitlements) {
      setEntitlements(cachedEntitlements);
    } else {
      fetchEntitlements();
    }
  };

  useEffect(() => {
    const validatedEntitlements =
      validateEntitlementsResponse(entitlementsData);

    setItem(ENTITLEMENTS, JSON.stringify(validatedEntitlements));
    setEntitlements(validatedEntitlements);
  }, [entitlementsData]);

  return {
    entitlementsLoading,
    fetchEntitlements,
    getEntitlements,
    pollEntitlements,
    readyToFetchEntitlements,
    setEntitlementsLoading,
  };
};

export default useHandleEntitlements;
